import React, { useState } from 'react';
import { Mail, Lock, ArrowRight, ShieldCheck } from 'lucide-react';
import logo from '../assest/logo.png';
import hrIllustration from '../assest/hr_illustration.png';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AdminLogin = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/login`, {
        email,
        password
      });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (setUser) setUser(response.data.user);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-100 to-emerald-50 flex items-center justify-center p-4"
      style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes floatCard { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        .animate-fade-slide { animation: fadeSlideIn 0.5s ease forwards; }
        .float-card { animation: floatCard 4s ease-in-out infinite; }
        .float-card-delay { animation: floatCard 4s ease-in-out 1.2s infinite; }
        .float-card-delay2 { animation: floatCard 4s ease-in-out 2.4s infinite; }
      `}</style>

      {/* Card — compact, no min-h tall */}
      <div className="w-full max-w-[800px] bg-white rounded-2xl shadow-[0_24px_80px_-12px_rgba(0,0,0,0.18)] overflow-hidden flex flex-col md:flex-row animate-fade-slide">

        {/* ── LEFT: Login Form ── */}
        <div className="w-full md:w-[45%] p-8 md:p-10 flex flex-col justify-center">
          {/* Logo */}
          <div className="mb-6">
            <img src={logo} alt="HRMS Software" className="h-8 w-auto object-contain mb-5" />
            <h1 className="text-[22px] font-bold text-slate-800 tracking-tight leading-none">Welcome Back</h1>
            <p className="text-[12px] text-slate-400 font-medium mt-1.5">Please enter your details to sign in.</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-[11px] font-semibold">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-0.5">Email or Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-300">
                  <Mail size={15} />
                </div>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@company.com"
                  className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-400/10 h-10 pl-10 pr-4 rounded-lg text-[13px] font-medium text-slate-700 transition-all outline-none"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-0.5">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-300">
                  <Lock size={15} />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-400/10 h-10 pl-10 pr-4 rounded-lg text-[13px] font-medium text-slate-700 transition-all outline-none"
                  required
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 bg-[#0f172a] hover:bg-slate-950 disabled:opacity-60 text-white font-bold h-10 rounded-lg shadow-lg shadow-slate-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-[12px] tracking-wide"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Authenticating...
                </span>
              ) : (
                <>Sign In <ArrowRight size={14} /></>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-[11px] font-medium text-slate-400">
            Don't have an account? <span className="text-emerald-500 cursor-pointer font-bold hover:underline ml-1">Contact Admin</span>
          </div>
        </div>

        {/* ── RIGHT: Illustration Panel ── */}
        <div className="hidden md:flex md:w-[55%] bg-gradient-to-br from-[#ecfdf5] to-[#d1fae5] flex-col items-center justify-center p-8 text-center relative overflow-hidden">

          {/* Decorative blobs */}
          <div className="absolute top-[-60px] right-[-60px] w-48 h-48 bg-emerald-300/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-[-40px] left-[-40px] w-40 h-40 bg-teal-200/30 rounded-full blur-2xl pointer-events-none" />

          {/* Real Illustration — fills nicely */}
          <div className="relative z-10 w-full max-w-[320px] mb-6">
            <img
              src={hrIllustration}
              alt="HR Management Illustration"
              className="w-full h-auto object-contain drop-shadow-2xl"
              style={{ maxHeight: '240px' }}
            />
          </div>

          {/* Text */}
          <h2 className="text-[18px] font-black text-slate-800 mb-1.5 tracking-tight">HR Management</h2>
          <p className="text-[11.5px] text-slate-500 font-medium max-w-[200px] mx-auto leading-relaxed">
            Next-gen workforce management & automation for enterprise scaling.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {['Attendance', 'Payroll', 'Tasks', 'Reports'].map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-white/70 border border-emerald-200 text-emerald-700 text-[10px] font-bold rounded-full shadow-sm backdrop-blur-sm"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Footer badge */}
          <div className="absolute bottom-5 flex items-center gap-1.5 text-[9px] font-black text-emerald-600/50 tracking-[0.3em] uppercase">
            <ShieldCheck size={11} /> Industrial Grade Data Security
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
